import { useEffect, useState, type ReactElement, type SyntheticEvent } from 'react';
import { AlertTriangle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

/**
 * Generic "footgun" type-to-confirm gate (PTV2-1412 double-step gating). The primary action stays
 * disabled until the operator types the exact confirmation word (case-sensitive, trimmed for paste
 * tolerance) — same shape as {@code WipeTenantConfirmDialog}, generalised so one component can gate
 * several distinct auth-provider settings (enabling JIT provisioning, activating new accounts
 * immediately, assigning a role) with per-action copy supplied by the caller.
 */
export function TypeToConfirmDialog({
  open,
  title,
  description,
  instructions,
  placeholder,
  confirmWord,
  confirmLabel,
  cancelLabel,
  onCancel,
  onConfirm,
}: {
  readonly open: boolean;
  readonly title: string;
  readonly description: string;
  readonly instructions: string;
  readonly placeholder: string;
  /** The exact (case-sensitive) word the operator must type to enable the confirm button. */
  readonly confirmWord: string;
  readonly confirmLabel: string;
  readonly cancelLabel: string;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}): ReactElement {
  const [typed, setTyped] = useState('');
  const matches = typed.trim() === confirmWord;

  // Reset the typed phrase whenever the dialog closes or is re-targeted at a different action.
  useEffect(() => {
    if (!open) setTyped('');
  }, [open]);

  function handleOpenChange(next: boolean): void {
    if (!next) onCancel();
  }

  function handleSubmit(event: SyntheticEvent): void {
    event.preventDefault();
    if (!matches) return;
    onConfirm();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" aria-hidden="true" />
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} noValidate className="space-y-3">
          <label htmlFor="type-to-confirm-input" className="block text-sm">
            {instructions}
          </label>
          <Input
            id="type-to-confirm-input"
            value={typed}
            onChange={(e) => {
              setTyped(e.target.value);
            }}
            placeholder={placeholder}
            autoComplete="off"
            spellCheck={false}
            className="font-mono"
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel}>
              {cancelLabel}
            </Button>
            <Button type="submit" variant="destructive" disabled={!matches}>
              {confirmLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
